// Day drill-down for the unified calendar: a month cell can only show a few
// chips ("+6" overflow), so clicking the overflow (or the day number) opens
// this panel listing EVERY event on that day; clicking one opens the full
// event detail sheet.
import React from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/features/shared/components/ui/sheet';
import { CALENDAR_TYPE_META, type WorkforceCalendarEvent } from '@/lib/calendarApi';

const STATUS_HINT: Record<string, string> = {
  pending: 'Pending', overdue: 'Overdue', at_risk: 'At risk',
  completed: 'Completed', approved: 'Approved',
};

interface Props {
  dayIso: string | null;
  events: WorkforceCalendarEvent[];
  onClose: () => void;
  onSelect: (event: WorkforceCalendarEvent) => void;
}

export const DayEventsSheet: React.FC<Props> = ({ dayIso, events, onClose, onSelect }) => {
  if (!dayIso) return null;
  const dayLabel = new Date(`${dayIso}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
  const sorted = [...events].sort((a, b) =>
    Number(b.allDay) - Number(a.allDay) || a.startAt.localeCompare(b.startAt));
  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  return (
    <Sheet open onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-left">{dayLabel}</SheetTitle>
          <SheetDescription className="text-left">
            {sorted.length} {sorted.length === 1 ? 'event' : 'events'} on this day
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-2">
          {sorted.map((ev) => {
            const meta = CALENDAR_TYPE_META[ev.type];
            const hint = STATUS_HINT[ev.status];
            return (
              <button
                key={ev.id}
                type="button"
                onClick={() => onSelect(ev)}
                className="w-full flex items-start gap-2.5 text-left rounded-lg border border-border bg-card hover:bg-muted px-3 py-2"
              >
                <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${meta.dot}`} aria-hidden />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-foreground truncate">{ev.title}</span>
                  <span className="block text-xs text-muted-foreground">
                    {meta.label}
                    {!ev.allDay && ` · ${fmtTime(ev.startAt)}${ev.endAt ? ` – ${fmtTime(ev.endAt)}` : ''}`}
                    {hint && ` · ${hint}`}
                  </span>
                </span>
              </button>
            );
          })}
          {sorted.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">Nothing scheduled on this day.</div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DayEventsSheet;
